import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, Link } from 'react-router-dom';
import {
    UploadCloud,
    FileText,
    ImageIcon,
    CheckCircle2,
    AlertTriangle,
    ArrowRight,
    Copy,
    Loader2,
    X,
    Play,
    RotateCcw,
    Trash2,
    Eye,
    Pencil,
    FileEdit
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { BillPreviewDialog } from '../components/BillPreviewDialog';
import { ManualBillDialog } from '../components/ManualBillDialog';
import { api, fmtDate, fmtDateTime, fmtMoney } from '../lib/api';
import { publishUploadProgress } from '../lib/uploadProgress';
import { useUploadQueue } from '../context/UploadQueueContext';
import { consumeSharedFiles } from '../lib/shareTarget';
import { compressImageFile, compressImageFiles, computeFileHash } from '../lib/imageCompress';
import { PriceDeltaBadge, OldNewPricePills } from '../components/PriceDeltaBadge';

const MAX_BATCH = 20;
const MAX_BYTES = 15 * 1024 * 1024;
// Sprint 2026-07 (A.1 → A.1b): worker pool concurrency for the upload queue.
// Bumped from 3 → 5 based on user feedback (Daily Sheet parallel was fast; they
// wanted the same for bill uploads). 5 workers is still comfortably below the
// backend's connection budget and the phone's memory ceiling — most requests
// spend ~50s in Claude I/O, so CPU/RAM stays low.
const UPLOAD_CONCURRENCY = 5;

function uid() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function fileIsSupported(f) {
    const isPdf = f.type.includes('pdf') || /\.pdf$/i.test(f.name);
    const isImage = /image\/(png|jpe?g|webp)/.test(f.type) || /\.(png|jpg|jpeg|webp)$/i.test(f.name);
    return { ok: isPdf || isImage, isPdf, isImage };
}

function humanBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_META = {
    queued: { label: 'Queued', tone: 'muted' },
    uploading: { label: 'Uploading', tone: 'info' },
    processing: { label: 'Scanning', tone: 'info' },
    preview: { label: 'Review needed', tone: 'warning' },
    done: { label: 'Done', tone: 'success' },
    alerts: { label: 'Done · alerts', tone: 'warning' },
    duplicate: { label: 'Duplicate', tone: 'warning' },
    rejected: { label: 'Not a bill', tone: 'error' },
    error: { label: 'Failed', tone: 'error' },
    cancelled: { label: 'Cancelled', tone: 'muted' }
};

function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.queued;
    const cls = {
        muted: 'bg-muted text-muted-foreground border-border',
        info: 'bg-accent text-accent-foreground border-[hsl(var(--accent))]',
        success: 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success-fg))] border-[hsl(var(--success-border))]',
        warning: 'bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border-[hsl(var(--warning-border))]',
        error: 'bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] border-[hsl(var(--price-increase-border))]'
    }[m.tone];
    return (
        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${cls}`}>
            {(status === 'uploading' || status === 'processing') && <Loader2 size={12} className="animate-spin" />}
            {status === 'done' && <CheckCircle2 size={12} />}
            {status === 'alerts' && <AlertTriangle size={12} />}
            {status === 'duplicate' && <Copy size={12} />}
            {status === 'error' && <AlertTriangle size={12} />}
            <span>{m.label}</span>
        </span>
    );
}

export default function UploadPage() {
    const inputRef = useRef(null);
    const nav = useNavigate();
    const { refreshAlerts, workspace, workspaceLoaded, openBusinessSetup } = useOutletContext() || {};

    // Compliance gate (2026-01): a bill cannot be scanned until the tenant
    // has completed business setup with a valid PAN. This anchors every bill
    // to a compliant workspace before the OCR pipeline touches it. `workspace`
    // is null when the profile has never been created; when created, the
    // backend guarantees a 10-char PAN, so we accept anything with a
    // non-empty PAN string.
    const businessReady = Boolean(workspace && (workspace.pan || '').trim());

    // Sprint C (fix 2026-07): only open the setup dialog AFTER the workspace
    // load has completed. Before that fix, sharing a file from another app
    // triggered this useEffect on mount while `workspace` was still `null`
    // (the /workspace GET hadn't resolved yet), which flashed the "Add your
    // business" dialog for users whose profile was already fully configured.
    // Guard with a ref so we only fire ONCE per mount even if `workspaceLoaded`
    // re-renders.
    const businessGateFiredRef = useRef(false);
    useEffect(() => {
        if (!workspaceLoaded) return;               // wait for /workspace to resolve
        if (businessGateFiredRef.current) return;   // fire at most once per mount
        if (!businessReady && typeof openBusinessSetup === 'function') {
            businessGateFiredRef.current = true;
            openBusinessSetup();
        } else if (businessReady) {
            businessGateFiredRef.current = true;    // configured — never nag
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceLoaded, businessReady]);

    // Sprint 2026-01 (PWA): if the user reached this page via Android's
    // "Share to app" flow (POST /share-target → SW redirect ?share=<id>),
    // pull the shared file(s) out of IndexedDB and drop them straight into
    // the upload queue. Runs once on mount; no-op when no ?share= param.
    // We defer to `addFilesRef.current` to avoid a chicken-and-egg dep on
    // the `addFiles` useCallback which is defined further down.
    //
    // Fix 2026-07: wait for workspaceLoaded before deciding businessReady —
    // otherwise a shared file racing against the /workspace GET would show a
    // false "complete Business setup" toast even for configured users.
    const addFilesRef = useRef(null);
    const shareConsumedRef = useRef(false);
    useEffect(() => {
        if (!workspaceLoaded) return;
        if (shareConsumedRef.current) return;
        shareConsumedRef.current = true;
        let cancelled = false;
        consumeSharedFiles().then((files) => {
            if (cancelled || !files.length) return;
            if (!businessReady) {
                toast.error('Complete Business setup (PAN required) before we can process your shared bills.');
                openBusinessSetup?.();
                return;
            }
            if (addFilesRef.current) {
                addFilesRef.current(files);
                toast.success(`Received ${files.length} shared file${files.length === 1 ? '' : 's'} — processing…`);
            }
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceLoaded, businessReady]);
    const [dragging, setDragging] = useState(false);
    // Sprint 2026-07: queue + running state hoisted to <UploadQueueProvider>
    // so navigating to another tab mid-scan no longer resets the queue.
    // See src/context/UploadQueueContext.jsx for rationale.
    const {
        queue,
        setQueue,
        running,
        setRunning,
        runningRef,
        queueRef,
        patchItem,
    } = useUploadQueue();
    // Sprint C: multi-image single-bill mode. When ON, files added to the queue
    // do NOT auto-run. Instead, the user clicks "Combine as one bill" to fire
    // /bills/upload/multipage with all queued images at once.
    const [multipageMode, setMultipageMode] = useState(false);
    // Sprint 2026-08 — Manual bill entry
    const [manualDialogOpen, setManualDialogOpen] = useState(false);
    const [previewDup, setPreviewDup] = useState(null);
    // Phase 14: classifier rejection — { itemId, data }
    const [rejectedFor, setRejectedFor] = useState(null);
    // Phase 11: multi-page PDF review — { itemId, previewId, initial }
    const [previewFor, setPreviewFor] = useState(null);
    // NOTE: `runningRef` and `patchItem` are provided by useUploadQueue()
    // above so they survive UploadPage re-mounts (persistent upload queue).
    // Ease My Bill uses Claude Sonnet 5 exclusively (accurate + unified OCR pipeline).
    // The `model` field is retained in the FormData for backend backward-compat
    // but is always the single supported model.
    const modelChoice = 'claude-sonnet-5';

    const runOne = useCallback(
        async (item, { force = false } = {}) => {
            // Silent client-side compression before upload. Large phone-camera
            // images (8-15 MB) would otherwise blow past Cloudflare's ~100s
            // edge timeout during upload + Claude OCR, and the user would see
            // a scary 524 error. compressImageFile is a no-op for PDFs and
            // for images already under ~2 MB.
            let uploadFile = item.file;
            // Compute the ORIGINAL-file hash BEFORE compression so the
            // server can dedup on a stable identifier even when JPEG
            // re-encoding produces slightly different bytes (see
            // computeFileHash docblock for the full rationale).
            let clientFileHash = '';
            try {
                clientFileHash = await computeFileHash(item.file);
            } catch (e) {
                console.debug('[UploadPage] computeFileHash failed:', e);
            }
            try {
                uploadFile = await compressImageFile(item.file);
            } catch (e) {
                console.debug('[UploadPage] compressImageFile failed, using original:', e);
            }
            const fd = new FormData();
            fd.append('file', uploadFile, uploadFile.name);
            if (force) fd.append('force', '1');
            // Send the currently-selected OCR model with each upload
            fd.append('model', modelChoice);
            if (clientFileHash) fd.append('client_file_hash', clientFileHash);
            patchItem(item.id, { status: 'uploading', progress: 0, error: null });
            try {
                const { status, data } = await api.uploadBill(fd, (e) => {
                    if (e.total) {
                        const p = Math.min(100, Math.round((e.loaded / e.total) * 100));
                        patchItem(item.id, {
                            progress: p,
                            status: p >= 100 ? 'processing' : 'uploading'
                        });
                    }
                });
                if (status === 409 && data?.duplicate) {
                    patchItem(item.id, {
                        status: 'duplicate',
                        progress: 100,
                        duplicate: data,
                        result: null
                    });
                    // Sprint 2026-09: a queue-item badge alone is easy to
                    // miss when the user has scrolled away. Surface a
                    // prominent, actionable toast with the existing bill's
                    // key details so the duplicate is impossible to overlook.
                    const existing = data?.existing_bill || {};
                    const existingDate = existing.bill_date
                        ? new Date(existing.bill_date).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric',
                        })
                        : (existing.uploaded_at || '').slice(0, 10) || '—';
                    const existingTotal = typeof existing.total === 'number'
                        ? `₹${Number(existing.total).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                        : '';
                    const invSuffix = existing.invoice_number ? ` (#${existing.invoice_number})` : '';
                    toast.warning('Duplicate bill blocked', {
                        description:
                            `${existing.vendor || 'This bill'} on ${existingDate}${existingTotal ? ` for ${existingTotal}` : ''}${invSuffix} is already in your workspace. ` +
                            `Reason: ${data?.match_reason || 'same file'}.`,
                        duration: 8000,
                        action: {
                            label: 'Upload anyway',
                            onClick: () => forceOne(item.id),
                        },
                    });
                    return { outcome: 'duplicate' };
                }
                // Phase 14: classifier rejected this document as not-a-bill
                if (status === 422 && data?.rejected) {
                    patchItem(item.id, {
                        status: 'rejected',
                        progress: 100,
                        rejected: data,
                        result: null,
                        duplicate: null,
                    });
                    setRejectedFor({ itemId: item.id, data });
                    return { outcome: 'rejected' };
                }
                // Phase 11: multi-page PDF → server returned a preview payload.
                // Open the review modal; commit will create the actual bills.
                if (data?.preview && data?.preview_id) {
                    patchItem(item.id, {
                        status: 'preview',
                        progress: 100,
                        preview: data,
                        result: null,
                        duplicate: null,
                    });
                    setPreviewFor({ itemId: item.id, previewId: data.preview_id, initial: data });
                    return { outcome: 'preview' };
                }
                const hasAlerts = (data?.alerts || []).length > 0;
                patchItem(item.id, {
                    status: hasAlerts ? 'alerts' : 'done',
                    progress: 100,
                    result: data,
                    duplicate: null
                });
                return { outcome: 'done', alerts: (data?.alerts || []).length };
            } catch (e) {
                console.error('upload error', e);
                patchItem(item.id, {
                    status: 'error',
                    progress: 0,
                    error: e?.response?.data?.detail || e.message || 'Upload failed'
                });
                return { outcome: 'error' };
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [patchItem, modelChoice]
    );

    const processQueue = useCallback(async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        setRunning(true);
        // Synchronous claim set — prevents two workers from picking the same
        // item within the same microtask window (before React has flushed the
        // patchItem status change). Cleared implicitly when the queue empties.
        const claimed = new Set();
        try {
            // Sprint 2026-07 (A.1): parallel worker pool. Previously the queue
            // drained sequentially, so 5 bills × ~60s = ~5 minutes of the user
            // staring at the app. We now spin up UPLOAD_CONCURRENCY workers
            // that each pull the next `queued` item until the queue is empty.
            //
            // Each worker is a self-contained loop; they naturally load-balance
            // because faster items free up a slot sooner. We use a Set-based
            // synchronous claim (not React state) to guarantee no double-pick
            // during the tiny window between "picked" and "status=uploading".
            const worker = async () => {
                while (true) {
                    const next = queueSnapshot().find(
                        (it) => it.status === 'queued' && !claimed.has(it.id),
                    );
                    if (!next) return;
                    claimed.add(next.id);
                    // eslint-disable-next-line no-await-in-loop
                    await runOne(next);
                }
            };
            const workers = Array.from({ length: UPLOAD_CONCURRENCY }, () => worker());
            await Promise.all(workers);
        } finally {
            runningRef.current = false;
            setRunning(false);
            refreshAlerts?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runOne, refreshAlerts]);

    /**
     * Sprint C — combine all currently queued image files into a single bill.
     * Uses `POST /api/bills/upload/multipage`. Only images are combined; PDFs
     * or non-queued items are ignored (should never be queued in multipage mode).
     */
    const runCombined = useCallback(async () => {
        if (runningRef.current) return;
        const imgs = queueSnapshot().filter((it) => it.status === 'queued' && !it.isPdf);
        if (imgs.length < 2) {
            toast.error('Add at least 2 images to combine into one bill.');
            return;
        }
        if (imgs.length > 8) {
            toast.error('Multi-page bill supports at most 8 images.');
            return;
        }
        runningRef.current = true;
        setRunning(true);
        // Mark all as processing
        for (const it of imgs) {
            patchItem(it.id, { status: 'processing', progress: 10, error: null });
        }
        try {
            // Silent compression for every page. See rationale in runOne.
            let uploadFiles;
            // Compute per-page ORIGINAL hashes so the backend can detect a
            // re-upload of the same multi-page batch even if compression
            // re-encoded the images. We concatenate them into a single
            // batch-scoped hash string so the server can dedup by the
            // whole batch, not just one page.
            let batchFileHashes = '';
            try {
                const hashes = await Promise.all(imgs.map((it) => computeFileHash(it.file)));
                batchFileHashes = hashes.filter(Boolean).join(',');
            } catch (e) {
                console.debug('[UploadPage] batch computeFileHash failed:', e);
            }
            try {
                uploadFiles = await compressImageFiles(imgs.map((it) => it.file));
            } catch (e) {
                console.debug('[UploadPage] batch compression failed, using originals:', e);
                uploadFiles = imgs.map((it) => it.file);
            }
            const fd = new FormData();
            for (let i = 0; i < imgs.length; i++) {
                const f = uploadFiles[i] || imgs[i].file;
                fd.append('files', f, f.name);
            }
            fd.append('model', modelChoice);
            if (batchFileHashes) fd.append('client_file_hash', batchFileHashes);
            const { status, data } = await api.uploadMultipageBill(fd, (e) => {
                if (e.total) {
                    const p = Math.min(95, Math.round((e.loaded / e.total) * 90) + 5);
                    for (const it of imgs) patchItem(it.id, { progress: p });
                }
            });
            if (status === 409 && data?.duplicate) {
                for (const it of imgs) {
                    patchItem(it.id, {
                        status: 'duplicate', progress: 100,
                        duplicate: data, result: null,
                    });
                }
                const existing = data?.existing_bill || {};
                const existingDate = existing.bill_date
                    ? new Date(existing.bill_date).toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric',
                    })
                    : (existing.uploaded_at || '').slice(0, 10) || '—';
                const existingTotal = typeof existing.total === 'number'
                    ? `₹${Number(existing.total).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                    : '';
                toast.warning('Duplicate multi-page bill blocked', {
                    description:
                        `${existing.vendor || 'This bill'} on ${existingDate}${existingTotal ? ` for ${existingTotal}` : ''} is already saved.`,
                    duration: 8000,
                });
                return;
            }
            if (status === 422 && data?.rejected) {
                for (const it of imgs) {
                    patchItem(it.id, {
                        status: 'rejected', progress: 100, rejected: data, result: null,
                    });
                }
                toast.error('These pages don\'t look like a supplier bill.');
                return;
            }
            const alertsCount = (data?.alerts || []).length;
            // Attach the combined result to the first item, mark others as merged.
            const [first, ...rest] = imgs;
            patchItem(first.id, {
                status: alertsCount ? 'alerts' : 'done',
                progress: 100,
                result: data,
                combinedPages: imgs.length,
            });
            for (const it of rest) {
                patchItem(it.id, {
                    status: 'done', progress: 100,
                    result: null,
                    mergedInto: first.id,
                    combinedPageIndex: imgs.indexOf(it) + 1,
                });
            }
            toast.success(
                `Combined ${imgs.length} pages into one bill${alertsCount ? ` · ${alertsCount} alert${alertsCount !== 1 ? 's' : ''}` : ''}`,
            );
        } catch (e) {
            console.error('multipage upload error', e);
            const msg = e?.response?.data?.detail || e?.message || 'Multi-page upload failed';
            for (const it of imgs) patchItem(it.id, { status: 'error', error: msg, progress: 0 });
            toast.error(msg);
        } finally {
            runningRef.current = false;
            setRunning(false);
            refreshAlerts?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patchItem, refreshAlerts]);

    // helper — read queue from a ref via functional setter (avoid stale closure).
    // `queueRef` comes from UploadQueueContext so it survives UploadPage
    // remounts (the ref lives on the provider, not this component).
    function queueSnapshot() {
        return queueRef.current;
    }

    const addFiles = useCallback(
        (fileList) => {
            // Compliance gate: block file ingest until the workspace's PAN is
            // recorded. This runs BEFORE any file is queued so we never let a
            // bill enter the pipeline without a tenant/PAN anchor.
            if (!businessReady) {
                toast.error('Please complete Business setup (PAN required) before uploading bills.');
                try { openBusinessSetup?.(); } catch (e) { console.debug('[UploadPage] openBusinessSetup failed:', e); }
                return;
            }
            const incoming = Array.from(fileList || []);
            if (!incoming.length) return;
            const current = queueSnapshot();
            const remainingCap = Math.max(0, MAX_BATCH - current.length);
            if (incoming.length > remainingCap) {
                toast.warning(`Only the first ${remainingCap} of ${incoming.length} files were added (max ${MAX_BATCH} at a time).`);
            }
            const kept = incoming.slice(0, remainingCap);
            const items = [];
            const rejected = [];
            for (const f of kept) {
                const { ok, isPdf } = fileIsSupported(f);
                if (!ok) {
                    rejected.push(`${f.name} (unsupported type)`);
                    continue;
                }
                if (f.size > MAX_BYTES) {
                    rejected.push(`${f.name} (>15 MB)`);
                    continue;
                }
                items.push({
                    id: uid(),
                    file: f,
                    status: 'queued',
                    progress: 0,
                    isPdf,
                    result: null,
                    error: null,
                    duplicate: null,
                    rejected: null
                });
            }
            if (rejected.length) toast.error(`Skipped: ${rejected.join(', ')}`);
            if (items.length) setQueue((q) => [...q, ...items]);
            // In multi-page mode we DON'T auto-start; user hits "Combine as one bill" instead.
            if (items.length && !multipageMode) setTimeout(() => processQueue(), 50);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [processQueue, multipageMode, businessReady, openBusinessSetup]
    );

    // Keep the ref pointer up-to-date so the share-target intake effect
    // (which runs before addFiles is defined) can call the latest closure.
    useEffect(() => { addFilesRef.current = addFiles; }, [addFiles]);

    const onDrop = (ev) => {
        ev.preventDefault();
        setDragging(false);
        if (!businessReady) {
            toast.error('Please complete Business setup (PAN required) before uploading bills.');
            try { openBusinessSetup?.(); } catch (e) { console.debug('[UploadPage] openBusinessSetup failed:', e); }
            return;
        }
        addFiles(ev.dataTransfer?.files);
    };

    const onPick = (ev) => {
        addFiles(ev.target.files);
        ev.target.value = '';
    };

    const forceOne = useCallback(
        async (id) => {
            const it = queueSnapshot().find((x) => x.id === id);
            if (!it) return;
            await runOne(it, { force: true });
            refreshAlerts?.();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [runOne, refreshAlerts]
    );

    const forceAllDuplicates = useCallback(async () => {
        const dups = queueSnapshot().filter((x) => x.status === 'duplicate');
        for (const it of dups) {
            // eslint-disable-next-line no-await-in-loop
            await runOne(it, { force: true });
        }
        refreshAlerts?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runOne, refreshAlerts]);

    const retryOne = useCallback(
        async (id) => {
            const it = queueSnapshot().find((x) => x.id === id);
            if (!it) return;
            patchItem(id, { status: 'queued', error: null, progress: 0 });
            setTimeout(() => processQueue(), 50);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [patchItem, processQueue]
    );

    const removeOne = useCallback((id) => {
        setQueue((q) => q.filter((x) => x.id !== id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearFinished = useCallback(() => {
        setQueue((q) => q.filter((x) => !['done', 'alerts', 'error', 'cancelled'].includes(x.status)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearAll = useCallback(() => {
        if (running) {
            toast.error('Wait for current uploads to finish first.');
            return;
        }
        setQueue([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running]);

    // Aggregate summary
    const summary = useMemo(() => {
        const s = { total: queue.length, queued: 0, active: 0, done: 0, alerts: 0, duplicate: 0, error: 0, alertsCount: 0 };
        for (const it of queue) {
            if (it.status === 'queued') s.queued++;
            else if (it.status === 'uploading' || it.status === 'processing') s.active++;
            else if (it.status === 'done') s.done++;
            else if (it.status === 'alerts') {
                s.done++;
                s.alerts++;
                s.alertsCount += (it.result?.alerts || []).length;
            } else if (it.status === 'duplicate') s.duplicate++;
            else if (it.status === 'error') s.error++;
        }
        return s;
    }, [queue]);

    const isBatchDone = queue.length > 0 && summary.queued === 0 && summary.active === 0;

    // Sprint 2026-07 (A.2): publish the queue summary to the global upload
    // progress bus so the Topbar (and any other observer) can show a live
    // progress badge, even if the user has navigated away from /upload.
    // Also emit a "batch complete" toast once the queue drains.
    const prevActiveRef = useRef(0);
    useEffect(() => {
        publishUploadProgress(summary);
        // Batch-complete edge trigger: went from active>0 to active==0 with
        // at least one done/alerts item present.
        if (prevActiveRef.current > 0 && summary.active === 0 && summary.queued === 0
            && (summary.done + summary.error + summary.duplicate) > 0) {
            const parts = [];
            if (summary.done) parts.push(`${summary.done} saved`);
            if (summary.alertsCount) parts.push(`${summary.alertsCount} alerts`);
            if (summary.duplicate) parts.push(`${summary.duplicate} duplicate`);
            if (summary.error) parts.push(`${summary.error} failed`);
            if (parts.length) {
                toast.success(`Batch complete · ${parts.join(' · ')}`, { duration: 6000 });
            }
        }
        prevActiveRef.current = summary.active;
    }, [summary]);

    // Sprint 2026-07 (A.2): when the user navigates AWAY from /upload while
    // uploads are still active, clear the progress bus so a stale summary
    // doesn't linger in the Topbar. The uploads themselves are HTTP calls
    // that keep running server-side — they just won't be reflected in the
    // UI once the queue state is destroyed.
    useEffect(() => {
        return () => {
            // Only clear if we're actually unmounting mid-batch; otherwise
            // the final summary should stay visible so the Topbar can show
            // the "batch complete" state until the user acknowledges it.
            const s = window.__embUploadProgress;
            if (s && (s.active > 0 || s.queued > 0)) {
                publishUploadProgress({ total: 0, queued: 0, active: 0, done: 0, alerts: 0, duplicate: 0, error: 0, alertsCount: 0 });
            }
        };
    }, []);

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader
                title="Upload bills"
                subtitle={`Drop up to ${MAX_BATCH} files at a time — photos (JPG/PNG/WEBP) or PDFs. Ease My Bill scans them one by one and extracts vendor, date and every line item.`}
            />

            {/* Duplicate preview dialog */}
            <Dialog open={!!previewDup} onOpenChange={(o) => !o && setPreviewDup(null)}>
                <DialogContent data-testid="duplicate-preview-dialog" className="max-w-lg">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-full bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] flex items-center justify-center">
                                <Copy size={18} />
                            </div>
                            <DialogTitle className="font-heading">Duplicate bill</DialogTitle>
                        </div>
                        <DialogDescription>{previewDup?.message}</DialogDescription>
                    </DialogHeader>
                    {previewDup?.existing_bill && (
                        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1 text-sm">
                            <div className="font-medium">{previewDup.existing_bill.vendor}</div>
                            <div className="text-muted-foreground text-xs">
                                {previewDup.existing_bill.invoice_number
                                    ? `Invoice #${previewDup.existing_bill.invoice_number} · `
                                    : ''}
                                {fmtDate(previewDup.existing_bill.bill_date)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                Uploaded {fmtDateTime(previewDup.existing_bill.uploaded_at)} · Total{' '}
                                <span className="font-mono tabular-nums">
                                    {fmtMoney(previewDup.existing_bill.total, previewDup.existing_bill.currency)}
                                </span>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="ghost" onClick={() => setPreviewDup(null)}>
                            Close
                        </Button>
                        {previewDup?.existing_bill && (
                            <Link to={`/bills/${previewDup.existing_bill.id}`} onClick={() => setPreviewDup(null)}>
                                <Button variant="outline">Open existing bill</Button>
                            </Link>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase 14: Classifier rejection dialog — "not a bill" */}
            {/* Phase 18: also handles Ledger Guard route with CTA to /vendors */}
            <Dialog open={!!rejectedFor} onOpenChange={(o) => !o && setRejectedFor(null)}>
                <DialogContent data-testid="rejected-preview-dialog" className="max-w-lg">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-full bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] flex items-center justify-center">
                                <AlertTriangle size={18} />
                            </div>
                            <DialogTitle className="font-heading">
                                {rejectedFor?.data?.reason === 'buyer_ownership_mismatch'
                                    ? 'Bill is addressed to another business'
                                    : rejectedFor?.data?.route_to === 'vendor_reconciliation'
                                        ? 'This looks like a ledger'
                                        : "This doesn't look like a bill"}
                            </DialogTitle>
                        </div>
                        <DialogDescription>{rejectedFor?.data?.message}</DialogDescription>
                    </DialogHeader>
                    {/* Buyer-ownership mismatch details */}
                    {rejectedFor?.data?.reason === 'buyer_ownership_mismatch' && (
                        <div
                            className="rounded-lg border border-[hsl(var(--danger-border))] bg-[hsl(var(--danger-bg))]/40 p-3 space-y-2 text-sm"
                            data-testid="rejected-ownership-details"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                                    Bill&apos;s buyer {rejectedFor.data.mismatch?.kind === 'gstin' ? 'GSTIN' : 'PAN'}
                                </span>
                                <Badge variant="outline" className="font-mono text-xs">
                                    {rejectedFor.data.mismatch?.bill_value || '—'}
                                </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground text-xs uppercase tracking-wide">Your workspace PAN</span>
                                <Badge variant="outline" className="font-mono text-xs">
                                    {rejectedFor.data.mismatch?.workspace_pan || '—'}
                                </Badge>
                            </div>
                            {rejectedFor.data.extracted_preview?.buyer_name && (
                                <div className="text-xs text-muted-foreground italic pt-1 border-t border-border/60">
                                    Addressed to: {rejectedFor.data.extracted_preview.buyer_name}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Classifier / not-a-bill details */}
                    {rejectedFor?.data && rejectedFor?.data?.reason !== 'buyer_ownership_mismatch' && (
                        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground text-xs uppercase tracking-wide">Detected as</span>
                                <Badge variant="outline" className="font-mono text-xs">
                                    {(rejectedFor.data.detected_type || rejectedFor.data.detected_hint || 'OTHER')
                                        .toString()
                                        .replace(/_/g, ' ')}
                                </Badge>
                            </div>
                            {typeof rejectedFor.data.confidence === 'number' && (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Confidence</span>
                                    <span className="font-mono tabular-nums">
                                        {rejectedFor.data.confidence}%
                                    </span>
                                </div>
                            )}
                            {rejectedFor.data.detected_reason && (
                                <div className="text-xs text-muted-foreground italic pt-1 border-t border-border/60">
                                    &ldquo;{rejectedFor.data.detected_reason}&rdquo;
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button
                            variant="ghost"
                            onClick={() => setRejectedFor(null)}
                            data-testid="rejected-close-btn"
                        >
                            Close
                        </Button>
                        {rejectedFor?.data?.route_to === 'vendor_reconciliation' && (
                            <Button
                                variant="default"
                                data-testid="rejected-goto-vendors-btn"
                                onClick={() => {
                                    setRejectedFor(null);
                                    nav('/vendors');
                                }}
                            >
                                Go to Vendors
                            </Button>
                        )}
                        {/* Hard-block: no "Upload anyway" for buyer-ownership mismatch. */}
                        {rejectedFor?.data?.reason !== 'buyer_ownership_mismatch' && (
                            <Button
                                variant="outline"
                                data-testid="rejected-force-upload-btn"
                                onClick={async () => {
                                    const stash = rejectedFor;
                                    setRejectedFor(null);
                                    const item = queue.find((q) => q.id === stash.itemId);
                                    if (item) {
                                        // Re-upload with force flag to bypass classifier
                                        await runOne(item, { force: true });
                                        refreshAlerts?.();
                                    }
                                }}
                            >
                                Upload anyway
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Phase 11: multi-page PDF review dialog */}
            <BillPreviewDialog
                open={!!previewFor}
                onOpenChange={(v) => {
                    if (!v) setPreviewFor(null);
                }}
                previewId={previewFor?.previewId}
                initialPreview={previewFor?.initial}
                onCommitted={(res) => {
                    const itemId = previewFor?.itemId;
                    if (itemId) {
                        patchItem(itemId, {
                            status: (res?.alerts_count || 0) > 0 ? 'alerts' : 'done',
                            progress: 100,
                            preview: null,
                            result: {
                                bill: res.bills?.[0] || null,
                                alerts: [],
                                commit_summary: {
                                    created_count: res.created_count,
                                    duplicate_count: res.duplicate_count,
                                    alerts_count: res.alerts_count,
                                    mode: res.mode,
                                },
                            },
                        });
                    }
                    setPreviewFor(null);
                    refreshAlerts?.();
                }}
                onDiscarded={() => {
                    const itemId = previewFor?.itemId;
                    if (itemId) {
                        patchItem(itemId, {
                            status: 'cancelled',
                            preview: null,
                        });
                    }
                    setPreviewFor(null);
                }}
            />

            <div className={`upload-glow rounded-2xl ${!businessReady ? 'opacity-95' : ''}`}>
                <Card
                    data-testid="upload-dropzone"
                    onDragOver={(e) => {
                        if (!businessReady) return;
                        e.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => {
                        if (!businessReady) {
                            toast.error('Please complete Business setup (PAN required) before uploading bills.');
                            try { openBusinessSetup?.(); } catch (e) { console.debug('[UploadPage] openBusinessSetup failed:', e); }
                            return;
                        }
                        inputRef.current?.click();
                    }}
                    className={
                        'relative rounded-xl border border-dashed bg-card p-6 md:p-10 transition-colors ' +
                        (businessReady
                            ? 'cursor-pointer ' + (dragging ? 'border-primary bg-accent/60' : 'border-border hover:border-primary/50')
                            : 'cursor-not-allowed border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))]/40')
                    }
                >
                    <input
                        ref={inputRef}
                        data-testid="upload-file-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,application/pdf"
                        multiple
                        className="hidden"
                        disabled={!businessReady}
                        onChange={onPick}
                    />
                    {!businessReady && (
                        <div
                            className="mb-4 rounded-lg border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] px-4 py-3 text-sm flex items-start gap-3"
                            data-testid="upload-business-setup-required-banner"
                        >
                            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-medium">Business setup required</div>
                                <div className="text-xs mt-0.5 opacity-90">
                                    Add your business name and PAN before uploading bills. This anchors every bill to a compliant workspace for CA exports and 26AS reconciliation.
                                </div>
                                <Button
                                    size="sm"
                                    className="mt-2 gap-1.5"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        try { openBusinessSetup?.(); } catch (e) { console.debug('[UploadPage] openBusinessSetup failed:', e); }
                                    }}
                                    data-testid="upload-open-business-setup-btn"
                                >
                                    <Pencil size={14} /> Complete business setup
                                </Button>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col items-center justify-center gap-3 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
                            <UploadCloud size={22} />
                        </div>
                        <div className="font-heading text-lg">Drop bills here or click to browse</div>
                        <div className="text-sm text-muted-foreground max-w-md">
                            Supported: JPG, PNG, WEBP, PDF (single or multi-page). Up to {MAX_BATCH} files at once. Max 15 MB
                            each. Usually 5–15 seconds per file.
                        </div>
                        <div className="flex gap-2 mt-1">
                            <Badge variant="secondary" className="gap-1">
                                <ImageIcon size={12} /> Photos
                            </Badge>
                            <Badge variant="secondary" className="gap-1">
                                <FileText size={12} /> PDFs
                            </Badge>
                            <Badge variant="secondary" className="gap-1">
                                Batch up to {MAX_BATCH}
                            </Badge>
                        </div>
                        <div
                            className={`mt-3 flex items-start gap-3 p-3 rounded-lg border w-full max-w-md text-left ${multipageMode ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/20'}`}
                            data-testid="upload-multipage-toggle-row"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Switch
                                data-testid="upload-multipage-toggle"
                                checked={multipageMode}
                                onCheckedChange={setMultipageMode}
                                className="mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                                <Label className="text-sm font-medium cursor-pointer">
                                    Multi-page bill (combine images)
                                </Label>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    Turn on if the images you're about to upload are pages of a
                                    <strong> single</strong> bill. Uploads will not run until you click
                                    &quot;Combine as one bill&quot;. Consumes 1 scan.
                                </div>
                            </div>
                        </div>
                        <div
                            className="mt-3 flex items-start gap-3 p-3 rounded-lg border w-full max-w-md text-left border-border bg-muted/20"
                            data-testid="upload-model-info-row"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex-1 min-w-0">
                                <Label className="text-sm font-medium">
                                    OCR engine: AI
                                </Label>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    Every bill is read by our AI — our most accurate OCR engine, tuned for Indian distributor bills, GST math, and Basic Rate vs MRP columns.
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Sprint 2026-08 — Manual bill entry escape hatch.
                Placed below the drop-zone (outside the clickable Card) so
                clicking it doesn't accidentally open the file picker. */}
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <span>No paper bill?</span>
                <Button
                    variant="link"
                    size="sm"
                    onClick={() => {
                        if (!businessReady) {
                            toast.error('Please complete Business setup (PAN required) first.');
                            try { openBusinessSetup?.(); } catch (e) { console.debug(e); }
                            return;
                        }
                        setManualDialogOpen(true);
                    }}
                    className="gap-1.5 px-1 h-auto text-primary hover:text-primary/80"
                    data-testid="upload-type-manually-btn"
                >
                    <FileEdit size={13} /> Type it manually
                </Button>
            </div>

            <ManualBillDialog
                open={manualDialogOpen}
                onOpenChange={setManualDialogOpen}
                onCreated={() => {
                    // Reuse the existing alerts refresh so PriceDeltaBadges,
                    // vendor lists, and dashboards update immediately.
                    refreshAlerts?.();
                }}
            />

            {queue.length > 0 && (
                <Card className="p-5 border-border/80" data-testid="upload-queue-card">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                        <div>
                            <div className="font-heading text-lg">Upload queue</div>
                            <div className="text-xs text-muted-foreground">
                                {summary.total} file{summary.total > 1 ? 's' : ''} · {summary.done} scanned ·{' '}
                                {summary.duplicate} duplicate · {summary.error} failed{' '}
                                {summary.alertsCount > 0 && `· ${summary.alertsCount} price change${summary.alertsCount > 1 ? 's' : ''}`}
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {multipageMode && (
                                <Button
                                    variant="default"
                                    size="sm"
                                    onClick={runCombined}
                                    disabled={
                                        running
                                        || queue.filter((it) => it.status === 'queued' && !it.isPdf).length < 2
                                    }
                                    className="gap-1.5"
                                    data-testid="upload-combine-btn"
                                >
                                    <Play size={14} />
                                    Combine {queue.filter((it) => it.status === 'queued' && !it.isPdf).length} images as one bill
                                </Button>
                            )}
                            {summary.duplicate > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={forceAllDuplicates}
                                    data-testid="upload-force-all-duplicates"
                                >
                                    Upload all duplicates anyway
                                </Button>
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearFinished}
                                data-testid="upload-clear-finished"
                                disabled={summary.done === 0 && summary.error === 0}
                            >
                                Clear finished
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearAll}
                                data-testid="upload-clear-all"
                                disabled={running}
                            >
                                <Trash2 size={14} className="mr-1" /> Clear all
                            </Button>
                        </div>
                    </div>

                    <ul className="space-y-2" data-testid="upload-queue-list">
                        {queue.map((it) => (
                            <li
                                key={it.id}
                                data-testid={`upload-queue-item-${it.id}`}
                                className="border border-border rounded-lg p-3 flex flex-col gap-2"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                        {it.isPdf ? <FileText size={16} /> : <ImageIcon size={16} />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="text-sm font-medium truncate">{it.file.name}</div>
                                            <StatusBadge status={it.status} />
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {humanBytes(it.file.size)}
                                            {it.result?.bill?.vendor && (
                                                <>
                                                    {' · '}
                                                    <span>{it.result.bill.vendor}</span>
                                                    {it.result.bill.total != null && (
                                                        <>
                                                            {' · '}
                                                            <span className="font-mono tabular-nums">
                                                                {fmtMoney(it.result.bill.total, it.result.bill.currency)}
                                                            </span>
                                                        </>
                                                    )}
                                                    {' · '}
                                                    <span>{(it.result.bill.items || []).length} items</span>
                                                </>
                                            )}
                                            {it.duplicate?.existing_bill && (
                                                <>
                                                    {' · '}duplicate of{' '}
                                                    <span className="font-medium">
                                                        {it.duplicate.existing_bill.vendor}
                                                        {it.duplicate.existing_bill.invoice_number
                                                            ? ` #${it.duplicate.existing_bill.invoice_number}`
                                                            : ''}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {(it.status === 'uploading' || it.status === 'processing') && (
                                            <Progress className="mt-2" value={it.status === 'processing' ? Math.max(60, it.progress || 0) : it.progress || 0} />
                                        )}
                                        {it.error && (
                                            <div className="mt-2 text-xs text-[hsl(var(--price-increase-fg))]">
                                                {it.error}
                                            </div>
                                        )}
                                        {(it.status === 'done' || it.status === 'alerts') && it.result?.bill && (
                                            <div className="mt-3 rounded-md border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] p-2.5" data-testid={`upload-verify-callout-${it.id}`}>
                                                <div className="flex items-start gap-2">
                                                    <AlertTriangle size={14} className="mt-0.5 flex-none" />
                                                    <div className="text-xs leading-relaxed">
                                                        <div className="font-semibold mb-1">Please verify the extraction</div>
                                                        <div className="tabular-nums">
                                                            <span className="opacity-80">Date:</span> <strong>{it.result.bill.bill_date || 'not extracted'}</strong>
                                                            {' · '}
                                                            <span className="opacity-80">Total:</span> <strong>{fmtMoney(it.result.bill.total || 0)}</strong>
                                                            {' · '}
                                                            <span className="opacity-80">Items:</span> <strong>{(it.result.bill.items || []).length}</strong>
                                                            {(it.result.bill.global_discount_percent || it.result.bill.global_discount_inferred) && (
                                                                <>
                                                                    {' · '}
                                                                    <span className="opacity-80">Global disc:</span> <strong>{it.result.bill.global_discount_percent}%{it.result.bill.global_discount_inferred ? ' (inferred)' : ''}</strong>
                                                                </>
                                                            )}
                                                        </div>
                                                        <div className="mt-1 opacity-90">
                                                            OCR isn't 100% perfect. Tap <strong>Review</strong> to check dates, quantities, and discount — edit anything wrong before it becomes part of your ledger.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* Phase 16: GSTIN-match badge */}
                                        {it.result?.gstin_check && (it.status === 'done' || it.status === 'alerts') && (
                                            <div className="mt-2">
                                                {it.result.gstin_check.match === 'verified' && (
                                                    <span className="inline-flex items-center gap-1 text-xs bg-[hsl(var(--success-bg))] text-[hsl(var(--success-fg))] border border-[hsl(var(--success-border))] rounded-md px-2 py-0.5">
                                                        <CheckCircle2 size={12} /> Verified · billed to your GSTIN
                                                    </span>
                                                )}
                                                {it.result.gstin_check.match === 'verified_new_state' && (
                                                    <span className="inline-flex items-center gap-1 text-xs bg-[hsl(var(--success-bg))] text-[hsl(var(--success-fg))] border border-[hsl(var(--success-border))] rounded-md px-2 py-0.5">
                                                        <CheckCircle2 size={12} /> Same PAN · new state ({it.result.gstin_check.state || 'India'})
                                                    </span>
                                                )}
                                                {it.result.gstin_check.match === 'mismatch' && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-xs bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border border-[hsl(var(--warning-border))] rounded-md px-2 py-0.5"
                                                        title={`Bill's PAN ${it.result.gstin_check.bill_pan} ≠ your workspace PAN ${it.result.gstin_check.workspace_pan}`}
                                                    >
                                                        <AlertTriangle size={12} /> Different business — bill GSTIN {it.result.gstin_check.bill_gstin}
                                                    </span>
                                                )}
                                                {it.result.gstin_check.match === 'no_gstin_on_bill' && (
                                                    <span className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground border border-border rounded-md px-2 py-0.5">
                                                        No GSTIN on bill (small vendor)
                                                    </span>
                                                )}
                                                {it.result.gstin_check.match === 'no_workspace' && (
                                                    <span className="inline-flex items-center gap-1 text-xs bg-muted text-muted-foreground border border-border rounded-md px-2 py-0.5">
                                                        Set up your business profile to enable GSTIN matching
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {it.status === 'alerts' && it.result?.alerts?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {it.result.alerts.slice(0, 4).map((a) => (
                                                    <span
                                                        key={a.id}
                                                        className="inline-flex items-center gap-1 text-xs bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] border border-[hsl(var(--warning-border))] rounded-md px-2 py-0.5"
                                                    >
                                                        {a.item_name}: {a.previous_price} → {a.new_price}
                                                    </span>
                                                ))}
                                                {it.result.alerts.length > 4 && (
                                                    <span className="text-xs text-muted-foreground">
                                                        +{it.result.alerts.length - 4} more
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 justify-end">
                                    {(it.status === 'done' || it.status === 'alerts') && it.result?.bill?.id && (
                                        <>
                                            <Button
                                                size="sm"
                                                data-testid={`upload-view-${it.id}`}
                                                onClick={() => nav(`/bills/${it.result.bill.id}?verify=1`)}
                                            >
                                                <Eye size={14} className="mr-1" /> Review &amp; verify
                                            </Button>
                                        </>
                                    )}
                                    {it.status === 'duplicate' && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setPreviewDup(it.duplicate)}
                                                data-testid={`upload-view-duplicate-${it.id}`}
                                            >
                                                See existing
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => forceOne(it.id)}
                                                data-testid={`upload-force-${it.id}`}
                                            >
                                                Upload anyway
                                            </Button>
                                        </>
                                    )}
                                    {it.status === 'rejected' && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setRejectedFor({ itemId: it.id, data: it.rejected })}
                                                data-testid={`upload-view-rejected-${it.id}`}
                                            >
                                                See why
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => forceOne(it.id)}
                                                data-testid={`upload-force-rejected-${it.id}`}
                                            >
                                                Upload anyway
                                            </Button>
                                        </>
                                    )}
                                    {it.status === 'error' && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => retryOne(it.id)}
                                            data-testid={`upload-retry-${it.id}`}
                                        >
                                            <RotateCcw size={14} className="mr-1" /> Retry
                                        </Button>
                                    )}
                                    {['queued', 'duplicate', 'error', 'done', 'alerts', 'cancelled'].includes(it.status) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeOne(it.id)}
                                            data-testid={`upload-remove-${it.id}`}
                                        >
                                            <X size={14} className="mr-1" /> Remove
                                        </Button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>

                    {isBatchDone && (
                        <div
                            className="mt-5 rounded-lg border border-border bg-muted/30 p-4 flex flex-wrap items-center gap-3"
                            data-testid="upload-batch-summary"
                        >
                            <CheckCircle2 size={18} className="text-[hsl(var(--success-fg))]" />
                            <div className="text-sm">
                                <span className="font-medium">Batch complete.</span>{' '}
                                <span className="text-muted-foreground">
                                    {summary.done} added
                                    {summary.duplicate > 0 && `, ${summary.duplicate} duplicate`}
                                    {summary.error > 0 && `, ${summary.error} failed`}
                                    {summary.alertsCount > 0 &&
                                        `, ${summary.alertsCount} price change${summary.alertsCount > 1 ? 's' : ''} across the batch`}
                                    .
                                </span>
                            </div>
                            <div className="ml-auto flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => nav('/bills')}>
                                    View all bills <ArrowRight size={14} className="ml-1" />
                                </Button>
                                {summary.alertsCount > 0 && (
                                    <Button size="sm" onClick={() => nav('/alerts')}>
                                        View alerts
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
}
